import { Trans, useTranslation } from "react-i18next";
import { Link } from "~/components/ui/link";
import { Button } from "~/components/ui/button";
import { Picture } from "~/components/ui/picture";
import { LINE_BREAKS } from "~/components/ui/max-width-line-break";
import { cn } from "~/lib/utils";
import { imageUrl } from "~/lib/image-url";

// Plain public/ paths so Picture can serve the AVIF/WebP siblings. Flattened design exports:
// the mobile one carries a soft glow wider than the viewport.
const vansImg = imageUrl("/images/fleet/fleet-vans.png");
const vansImgMobile = imageUrl("/images/fleet/fleet-vans-mobile.png");

const BULLET_COUNT = 5;

// 16px bold CTAs, 52px tall: equal grid columns on mobile, 190px wide from lg.
const ctaClassName = "h-[52px] w-full px-4 py-4 text-base leading-5 tracking-[0.16px] lg:w-[190px] lg:px-6";

// Copy, bullets and CTAs beside an aerial shot of the fleet. Must render inside the .fleet
// token scope. Nothing here clips, so the mobile glow can run off-screen (main clips x).
export function FleetVehicleView() {
    const { t } = useTranslation("fleet");

    return (
        <section aria-labelledby="fleet-vehicle-view-title" className="py-8 lg:pt-[60px] lg:pb-0">
            <div className="wrap flex flex-col items-center gap-6 lg:flex-row lg:gap-12">
                <div className="relative z-10 flex w-full flex-col gap-6 lg:flex-1 lg:gap-8">
                    <div className="flex flex-col gap-2 lg:gap-4">
                        <h2
                            id="fleet-vehicle-view-title"
                            className="font-display text-text-heading text-xl leading-[1.15] font-extrabold lg:max-w-[444px] lg:text-4xl lg:leading-[1.1]"
                        >
                            {t("vehicle_view.title")}
                        </h2>
                        <p className="text-text-body-secondary lg:text-md text-sm leading-5 lg:leading-[1.1]">
                            {t("vehicle_view.body")}
                        </p>
                        <ul className="text-text-body-secondary lg:text-md list-disc ps-[21px] text-sm leading-5 lg:ps-[27px] lg:leading-[1.5]">
                            {Array.from({ length: BULLET_COUNT }, (_, i) => (
                                <li key={i}>
                                    <Trans t={t} i18nKey={`vehicle_view.bullets.${i}`} components={LINE_BREAKS} />
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="grid w-full grid-cols-2 gap-3 lg:flex lg:w-auto">
                        <Button asChild variant="primary-alt" className={cn(ctaClassName, "max-lg:order-last")}>
                            <Link data-sid="fleet-vehicle-view-build-quote" to="/fleet/quote">{t("vehicle_view.build_quote")}</Link>
                        </Button>
                        <Button asChild variant="secondary-alt" className={ctaClassName}>
                            <Link data-sid="fleet-vehicle-view-contact-sales" to="/contact">{t("vehicle_view.contact_sales")}</Link>
                        </Button>
                    </div>
                </div>

                {/* Each export overhangs its frame at the design's offsets (desktop: a 611x602 export
                    in a 600x601 frame); the frame is centered even where it is wider than `wrap`. */}
                <div className="pointer-events-none relative aspect-[375/376] w-[375px] max-w-none shrink-0 lg:aspect-[600/601] lg:w-[51%] lg:max-w-[600px]">
                    <Picture
                        src={vansImg}
                        mobileSrc={vansImgMobile}
                        desktopMedia="(min-width: 1024px)"
                        alt={t("vehicle_view.image_alt")}
                        loading="lazy"
                        className="absolute top-[-24.9%] left-[-21.4%] h-[147.06%] w-[147.79%] max-w-none lg:top-[-0.17%] lg:left-[-1.95%] lg:h-[100.05%] lg:w-[101.78%]"
                    />
                </div>
            </div>
        </section>
    );
}

export default FleetVehicleView;
